import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Snackbar,
  SelectChangeEvent,
  Stack,
} from '@mui/material';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  Pets as PetsIcon,
} from '@mui/icons-material';
import { animalService, CreateAnimalData } from '../../services/animalService';

interface AddAnimalFormProps {
  onSuccess?: () => void;
  onCancel?: () => void;
}

export const AddAnimalForm: React.FC<AddAnimalFormProps> = ({ onSuccess, onCancel }) => {
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');

  const [formData, setFormData] = useState<CreateAnimalData>({
    nom: '',
    date_naissance: '',
    espece: '',
    race: '',
    poids: undefined,
    sexe: 'M',
    sterilise: false,
    numero_puce: '',
    numero_tatouage: '',
    photo_url: '',
  });

  // Listes prédéfinies
  const especesCommon = [
    'Chien',
    'Chat',
    'Lapin',
    'Hamster',
    'Oiseau',
    'Poisson',
    'Reptile',
    'Furet',
    'Cochon d\'Inde',
    'Autre',
  ];

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.nom.trim()) {
      newErrors.nom = 'Le nom de votre animal est obligatoire';
    }

    if (!formData.espece.trim()) {
      newErrors.espece = 'L\'espèce est obligatoire';
    }

    if (formData.poids !== undefined && formData.poids <= 0) {
      newErrors.poids = 'Le poids doit être supérieur à 0';
    }

    if (formData.numero_puce && formData.numero_puce.length < 8) {
      newErrors.numero_puce = 'Le numéro de puce doit contenir au moins 8 caractères';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      setSubmitError('');

      const submitData: CreateAnimalData = {
        nom: formData.nom.trim(),
        date_naissance: formData.date_naissance || undefined,
        espece: formData.espece.trim(),
        race: formData.race?.trim() || undefined,
        poids: formData.poids,
        sexe: formData.sexe,
        sterilise: formData.sterilise,
        numero_puce: formData.numero_puce?.trim() || undefined,
        numero_tatouage: formData.numero_tatouage?.trim() || undefined,
        photo_url: formData.photo_url?.trim() || undefined,
      };

      await animalService.create(submitData);
      
      setSuccessMessage(`${formData.nom} a été ajouté avec succès !`);
      
      // Reset form
      setFormData({
        nom: '',
        date_naissance: '',
        espece: '',
        race: '',
        poids: undefined,
        sexe: 'M',
        sterilise: false,
        numero_puce: '',
        numero_tatouage: '',
        photo_url: '',
      });

      if (onSuccess) {
        setTimeout(() => {
          onSuccess();
        }, 1500);
      }
    } catch (error: any) {
      console.error('Erreur lors de l\'ajout:', error);
      const backendData = error?.response?.data;
      if (backendData) {
        const fieldErrors = (backendData.errors && typeof backendData.errors === 'object')
          ? backendData.errors
          : (typeof backendData === 'object' ? backendData : undefined);

        if (fieldErrors) {
          setErrors(fieldErrors as Record<string, string>);

          // Extraire un message global utile si présent
          const nonField = (fieldErrors as any).non_field_errors;
          const detail = (fieldErrors as any).detail;
          const proprietaireMsg = (fieldErrors as any).proprietaire;

          if (nonField) {
            setSubmitError(Array.isArray(nonField) ? nonField.join(' ') : String(nonField));
          } else if (detail) {
            setSubmitError(String(detail));
          } else if (proprietaireMsg) {
            setSubmitError(Array.isArray(proprietaireMsg) ? proprietaireMsg.join(' ') : String(proprietaireMsg));
          } else {
            // Si aucun champ affichable n'existe dans le formulaire, concaténer les messages
            const unknownMessages = Object.entries(fieldErrors)
              .filter(([key]) => !['nom','espece','race','poids','sexe','sterilise','numero_puce','numero_tatouage','photo_url','date_naissance'].includes(key))
              .map(([_, val]) => Array.isArray(val) ? val.join(' ') : String(val));
            if (unknownMessages.length) {
              setSubmitError(unknownMessages.join(' '));
            }
          }
          return;
        }
      }
      setSubmitError('Erreur lors de l\'ajout de l\'animal. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof CreateAnimalData) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const value = event.target.value;
    let processedValue: any = value;

    if (field === 'poids') {
      processedValue = value ? parseFloat(value) : undefined;
    }

    setFormData(prev => ({ ...prev, [field]: processedValue }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleSelectChange = (field: keyof CreateAnimalData) => (
    event: SelectChangeEvent<string>
  ) => {
    const value = event.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));
    
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleSwitchChange = (field: keyof CreateAnimalData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.checked;
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <Paper elevation={3} sx={{ p: 3, maxWidth: 800, mx: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <PetsIcon sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
        <Typography variant="h4" component="h1">
          Ajouter un animal
        </Typography>
      </Box>

      {submitError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {submitError}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit}>
        <Stack spacing={3}>
          {/* Informations de base */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom color="primary">
                Informations de base
              </Typography>
              
              <Stack spacing={2}>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <TextField
                    sx={{ minWidth: 200, flex: 1 }}
                    label="Nom de l'animal *"
                    value={formData.nom}
                    onChange={handleInputChange('nom')}
                    error={!!errors.nom}
                    helperText={errors.nom}
                    placeholder="ex: Médor, Félix..."
                  />
                  
                  <FormControl sx={{ minWidth: 200, flex: 1 }} error={!!errors.espece}>
                    <InputLabel>Espèce *</InputLabel>
                    <Select
                      value={formData.espece}
                      onChange={handleSelectChange('espece')}
                      label="Espèce *"
                    >
                      {especesCommon.map((espece) => (
                        <MenuItem key={espece} value={espece}>
                          {espece}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.espece && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                        {errors.espece}
                      </Typography>
                    )}
                  </FormControl>
                </Box>

                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <TextField
                    sx={{ minWidth: 200, flex: 1 }}
                    label="Race"
                    value={formData.race}
                    onChange={handleInputChange('race')}
                    placeholder="ex: Labrador, Persan..."
                  />

                  <TextField
                    sx={{ minWidth: 200, flex: 1 }}
                    label="Date de naissance"
                    type="date"
                    value={formData.date_naissance}
                    onChange={handleInputChange('date_naissance')}
                    InputLabelProps={{ shrink: true }}
                  />
                </Box>
              </Stack>
            </CardContent>
          </Card>

          {/* Caractéristiques physiques */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom color="primary">
                Caractéristiques physiques
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                <FormControl sx={{ minWidth: 120 }}>
                  <InputLabel>Sexe</InputLabel>
                  <Select
                    value={formData.sexe}
                    onChange={handleSelectChange('sexe')}
                    label="Sexe"
                  >
                    <MenuItem value="M">Mâle</MenuItem>
                    <MenuItem value="F">Femelle</MenuItem>
                  </Select>
                </FormControl>

                <TextField
                  sx={{ minWidth: 150 }}
                  label="Poids (kg)"
                  type="number"
                  value={formData.poids || ''}
                  onChange={handleInputChange('poids')}
                  error={!!errors.poids}
                  helperText={errors.poids}
                  inputProps={{ min: 0, step: 0.1 }}
                />

                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.sterilise}
                      onChange={handleSwitchChange('sterilise')}
                      color="primary"
                    />
                  }
                  label="Stérilisé(e)"
                />
              </Box>
            </CardContent>
          </Card>

          {/* Identification */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom color="primary">
                Identification (optionnel)
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <TextField
                  sx={{ minWidth: 200, flex: 1 }}
                  label="Numéro de puce"
                  value={formData.numero_puce}
                  onChange={handleInputChange('numero_puce')}
                  error={!!errors.numero_puce}
                  helperText={errors.numero_puce || "Minimum 8 caractères"}
                  placeholder="250269801234567"
                />

                <TextField
                  sx={{ minWidth: 200, flex: 1 }}
                  label="Numéro de tatouage"
                  value={formData.numero_tatouage}
                  onChange={handleInputChange('numero_tatouage')}
                  placeholder="ABC123"
                />
              </Box>
            </CardContent>
          </Card>

          {/* Boutons d'action */}
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 2 }}>
            <Button
              variant="contained"
              type="submit"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
              size="large"
              sx={{ minWidth: 150 }}
            >
              {loading ? 'Ajout...' : 'Ajouter l\'animal'}
            </Button>
            
            {onCancel && (
              <Button
                variant="outlined"
                onClick={onCancel}
                disabled={loading}
                startIcon={<CancelIcon />}
                size="large"
              >
                Annuler
              </Button>
            )}
          </Box>
        </Stack>
      </Box>

      <Snackbar
        open={!!successMessage}
        autoHideDuration={3000}
        onClose={() => setSuccessMessage('')}
      >
        <Alert severity="success" onClose={() => setSuccessMessage('')}>
          {successMessage}
        </Alert>
      </Snackbar>
    </Paper>
  );
}; 